import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { currentWeek } from "@/lib/week";
import { useSmal } from "@/lib/useSmal";

const DAGEN = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const NL_DAG = { maandag: 0, dinsdag: 1, woensdag: 2, donderdag: 3, vrijdag: 4, zaterdag: 5, zondag: 6 };

const vandaagIdx = () => NL_DAG[new Intl.DateTimeFormat("nl-NL", { weekday: "long", timeZone: "Europe/Amsterdam" }).format(new Date()).toLowerCase()];
const openTaken = (taken, checks) => (taken || []).filter((t) => !checks[t.id]).length;

// v18-R1 R4: vandaag als die taken heeft; anders de eerstvolgende dag met taken; anders de laatste eerdere dag met open taken.
const startDag = (dagen, grouped, checks, vi) => {
  const idx = (d) => DAGEN.indexOf(d);
  if (dagen.includes(DAGEN[vi])) return DAGEN[vi];
  return dagen.find((d) => idx(d) > vi) || [...dagen].reverse().find((d) => idx(d) < vi && openTaken(grouped[d], checks) > 0);
};

// v18 B2: op de telefoon staat vandaag open en zijn de andere dagen ingeklapt; voorbije dagen met open taken krijgen een teller.
export function useWeekDagen({ week, sleutel, dagen, grouped, checks, klaar }) {
  const smal = useSmal();
  const [open, setOpen] = useState(null);
  const [voor, setVoor] = useState("");
  const huidig = week === currentWeek();
  const vi = vandaagIdx();
  const voorbij = (dag) => week < currentWeek() || (huidig && DAGEN.indexOf(dag) < vi);
  useEffect(() => {
    if (!klaar || voor === sleutel) return;
    const eerste = huidig ? startDag(dagen, grouped, checks, vi) : dagen.find((d) => openTaken(grouped[d], checks) > 0);
    setOpen({ [eerste]: true });
    setVoor(sleutel);
  }, [klaar, sleutel, voor, huidig, vi, dagen, grouped, checks]);
  const isOpen = (dag) => !smal || (voor === sleutel && !!open?.[dag]);
  const toggle = (dag) => setOpen((o) => ({ ...(o || {}), [dag]: !o?.[dag] }));
  return { smal, isOpen, toggle, voorbij };
}

export const DagKop = ({ dag, taken, checks, smal, open, onToggle, voorbij, prefix, extra = 0 }) => {
  if (!smal) return <div className="px-3 py-2 bg-zinc-950 text-white font-heading text-xs font-black uppercase tracking-[.06em]">{dag}</div>;
  const n = openTaken(taken, checks);
  const klaar = taken.length - n;
  return (
    <button type="button" data-testid={`${prefix}-dag-${dag}`} aria-expanded={open} onClick={onToggle}
      className="w-full min-h-[44px] px-3 py-2 bg-zinc-950 text-white flex items-center gap-2 text-left">
      <span className="flex-1 font-heading text-xs font-black uppercase tracking-[.06em]">{dag}</span>
      {!open && extra > 0 && <span data-testid={`${prefix}-dag-extra-${dag}`} className="text-[11px] text-zinc-300">· {extra} extra</span>}
      {voorbij && n > 0 && <span data-testid={`${prefix}-dag-open-${dag}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-amber-950">{n} open</span>}
      {taken.length > 0 && <span data-testid={`${prefix}-dag-teller-${dag}`} className="font-mono text-[10.5px] leading-4 bg-white text-zinc-950 rounded px-1.5 tabular-nums">{klaar}/{taken.length}</span>}
      <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
};
